/** Settings → MCP (desktop only): Claude Desktop access on/off and its configuration. */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCopy, RotateCcw } from 'lucide-react';
import { useStore } from '../db/context';
import { mcpInfo, renewMcpToken, setMcpEnabled, type McpInfo } from './bridge';

export function McpSettings() {
  const { t } = useTranslation();
  const store = useStore();
  const [info, setInfo] = useState<McpInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void mcpInfo().then(setInfo);
  }, []);
  if (!info) return null;

  const config = JSON.stringify(
    { mcpServers: { 'nexus-open': { command: info.exe, args: ['--mcp'] } } },
    null,
    2,
  );

  return (
    <div className="flex flex-col gap-3 text-sm">
      <label className="flex items-center justify-between gap-3">
        <span>
          {t('mcp.enable')}
          <span className="block text-xs text-fg-muted">
            {info.running ? t('mcp.running', { port: info.port }) : t('mcp.off')}
          </span>
        </span>
        <input
          type="checkbox"
          checked={info.running}
          onChange={async (e) => {
            const want = e.target.checked;
            setError(null);
            setInfo({ ...info, running: want }); // right away; the real state follows
            try {
              setInfo(await setMcpEnabled(store, want));
            } catch (err) {
              setError(String(err));
              setInfo(await mcpInfo());
            }
          }}
        />
      </label>
      {error && (
        <p role="alert" className="rounded-md bg-red-500/10 px-2 py-1.5 text-xs text-danger">
          {t('mcp.error', { error })}
        </p>
      )}
      {info.running && (
        <>
          <p className="text-xs text-fg-muted">{t('mcp.howto')}</p>
          <pre className="overflow-x-auto rounded-md bg-bg-subtle p-2 text-xs">{config}</pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(config);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover"
            >
              <ClipboardCopy className="size-3.5" />
              {copied ? t('mcp.copied') : t('mcp.copy')}
            </button>
            <button
              type="button"
              onClick={async () => setInfo(await renewMcpToken())}
              className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover"
            >
              <RotateCcw className="size-3.5" />
              {t('mcp.renew')}
            </button>
          </div>
          <p className="text-xs text-fg-muted">{t('mcp.privacy')}</p>
        </>
      )}
    </div>
  );
}
